import { useState } from 'react';
import {
  Button,
  Input,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import type { ExporterReceiver } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { AddClientModal } from '../../components/settings/AddClientModal';

const NEW_CLIENT = '__new__';

/**
 * Agrega un cliente a Gestionar compromisos: uno de la exportadora que todavía
 * no está en el programa o, al final de la lista, uno nuevo (el mismo modal de
 * Configuración). La lista va dentro del modal y no en un popover: en Safari
 * de iPhone las opciones de un popover dentro de un modal no se dejan tocar.
 */
export default function AddProgramClientModal({
  isOpen,
  onClose,
  exporterId,
  addableReceivers,
  allReceivers,
  onAdd,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  exporterId: number;
  /** Clientes de la exportadora que no están en el programa. */
  addableReceivers: ExporterReceiver[];
  /** Todos, para no crear un nombre repetido. */
  allReceivers: ExporterReceiver[];
  onAdd: (receiverId: number) => void;
  onCreated: (receiver: ExporterReceiver) => void;
}) {
  const { t } = useTranslation('commitments');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const query = search.trim().toUpperCase();
  const items = [
    ...addableReceivers
      .filter((r) => r.name.toUpperCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({ key: String(r.id), label: r.name })),
    { key: NEW_CLIENT, label: t('+ Crear cliente nuevo') },
  ];

  const select = (key: string | number) => {
    if (key === NEW_CLIENT) {
      setCreating(true);
      return;
    }
    onAdd(Number(key));
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen && !creating} onClose={onClose}>
        <ModalContent>
          <ModalHeader>{t('Agregar cliente al programa')}</ModalHeader>
          <ModalBody>
            <p className='text-sm text-default-500'>
              {t(
                'El cliente aparece en la tabla para cargarle compromisos. Si no le cargas ninguno, deja de mostrarse.'
              )}
            </p>
            <Input
              label={t('Buscar cliente')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
              startContent={
                <Icon icon='tabler:search' className='text-default-400' width={16} />
              }
            />
            <div className='max-h-64 overflow-y-auto rounded-medium border border-default-200'>
              <Listbox
                aria-label={t('Clientes')}
                items={items}
                onAction={select}
              >
                {(item) => (
                  <ListboxItem
                    key={item.key}
                    className={item.key === NEW_CLIENT ? 'text-primary' : undefined}
                    startContent={
                      item.key === NEW_CLIENT ? (
                        <Icon icon='tabler:user-plus' width={16} />
                      ) : undefined
                    }
                  >
                    {item.label}
                  </ListboxItem>
                )}
              </Listbox>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={onClose}>
              {t('Cancelar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AddClientModal
        isOpen={isOpen && creating}
        onClose={() => {
          setCreating(false);
          onClose();
        }}
        exporterId={exporterId}
        existingClientNames={allReceivers.map((r) => r.name)}
        onClientAdded={onCreated}
      />
    </>
  );
}
